/**
 * Twitter Data Import Parser
 * Ported from backend importer.ts to run in the browser
 */

export interface TwitterExportData {
  formatName: string;
  data?: {
    data?: Array<{
      tableName: string;
      rows: Array<TweetData | CaptureData | UserData>;
    }>;
  };
}

export interface TweetData {
  rest_id: string;
  legacy?: {
    full_text?: string;
    created_at?: string;
    favorite_count?: number;
    retweet_count?: number;
    bookmark_count?: number;
    quote_count?: number;
    reply_count?: number;
    favorited?: boolean;
    bookmarked?: boolean;
    lang?: string;
    is_quote_status?: boolean;
    source?: string;
    entities?: {
      media?: Array<{
        type: string;
        media_url_https: string;
        video_info?: {
          variants?: Array<{
            url?: string;
          }>;
        };
      }>;
    };
    extended_entities?: {
      media?: Array<{
        type: string;
        media_url_https: string;
        video_info?: {
          variants?: Array<{
            url?: string;
          }>;
        };
      }>;
    };
  };
  core?: {
    user_results?: {
      result?: {
        core?: {
          screen_name?: string;
          name?: string;
        };
        avatar?: {
          image_url?: string;
        };
        legacy?: {
          name?: string;
          profile_image_url_https?: string;
        };
      };
    };
  };
}

export interface CaptureData {
  id: string;
  extension: string;
  type: string;
  data_key: string;
  created_at?: number;
}

export interface UserData {
  rest_id: string;
  core?: {
    screen_name?: string;
    name?: string;
  };
  avatar?: {
    image_url?: string;
  };
  legacy?: {
    name?: string;
    description?: string;
    followers_count?: number;
    friends_count?: number;
    favourites_count?: number;
    statuses_count?: number;
    profile_image_url_https?: string;
  };
}

export interface ParsedTables {
  tweets: TweetData[];
  captures: CaptureData[];
  users: UserData[];
}

/**
 * Validate that the JSON is a valid Twitter export
 */
export function validateTwitterExport(data: unknown): data is TwitterExportData {
  if (!data || typeof data !== 'object') return false;
  const exportData = data as TwitterExportData;
  if (exportData.formatName !== 'dexie') return false;
  if (!exportData.data?.data) return false;
  return Array.isArray(exportData.data.data);
}

/**
 * Parse tweet created_at string to timestamp (milliseconds)
 */
export function parseCreatedAt(dateStr?: string): number | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.getTime();
}

/**
 * Extract media URLs from tweet entities
 */
export function extractMediaUrls(tweet: TweetData): string | null {
  const mediaUrls: Array<{
    type: string;
    url: string;
    video_url?: string | null;
  }> = [];

  const legacy = tweet.legacy || {};

  if (legacy.entities?.media) {
    for (const media of legacy.entities.media) {
      if (media.media_url_https) {
        mediaUrls.push({
          type: media.type,
          url: media.media_url_https,
          video_url: media.video_info?.variants?.[0]?.url || null
        });
      }
    }
  }

  if (legacy.extended_entities?.media) {
    for (const media of legacy.extended_entities.media) {
      if (media.media_url_https && !mediaUrls.find(m => m.url === media.media_url_https)) {
        mediaUrls.push({
          type: media.type,
          url: media.media_url_https,
          video_url: media.video_info?.variants?.[0]?.url || null
        });
      }
    }
  }

  return mediaUrls.length > 0 ? JSON.stringify(mediaUrls) : null;
}

/**
 * Extract tables (tweets, captures, users) from Twitter export JSON
 */
export function extractTables(data: TwitterExportData): ParsedTables {
  const tables = data.data?.data || [];
  let tweets: TweetData[] = [];
  let captures: CaptureData[] = [];
  let users: UserData[] = [];

  for (const table of tables) {
    if (table.tableName === 'tweets') {
      tweets = table.rows as TweetData[];
    } else if (table.tableName === 'captures') {
      captures = table.rows as CaptureData[];
    } else if (table.tableName === 'users') {
      users = table.rows as UserData[];
    }
  }

  return { tweets, captures, users };
}

/**
 * Build a lookup map: tweetId -> captures[]
 */
export function buildCapturesMap(captures: CaptureData[]): Map<string, CaptureData[]> {
  const map = new Map<string, CaptureData[]>();
  for (const capture of captures) {
    if (capture.data_key) {
      if (!map.has(capture.data_key)) {
        map.set(capture.data_key, []);
      }
      map.get(capture.data_key)!.push(capture);
    }
  }
  return map;
}

/**
 * Check if a tweet should be imported based on its associated captures
 * Only import tweets that have LikesModule or BookmarksModule captures
 */
export function shouldImportTweet(
  tweetId: string,
  capturesMap: Map<string, CaptureData[]>
): boolean {
  const captures = capturesMap.get(tweetId);
  if (!captures) return false;
  return captures.some(c =>
    c.extension === 'LikesModule' || c.extension === 'BookmarksModule'
  );
}
